import type { FC } from "react";
import styles from "@/components/Avatar.module.css";

interface AvatarProps {
  name: string;
  // "gradient" (default) preserves every existing caller's look (e.g.
  // ProfileMenu). "colored" picks a flat accent deterministically from the
  // name, for contexts showing many avatars side by side (e.g. a user list)
  // where a uniform gradient on every row reads as one undifferentiated
  // blob rather than distinct people.
  variant?: "gradient" | "colored";
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Flat palette colors only (Chart & Layout Spec v2) — never --color-success
// (reserved for CTAs/confirmation, not decorative use) and never a gradient
// (that's the "gradient" variant's own look).
const COLORED_VARIANT_CLASSES = [
  styles.colorPurple,
  styles.colorPlum,
  styles.colorOrange,
  styles.colorPink,
];

// Bounded to a 31-bit range on every step (instead of letting the running
// total grow with the string's length) so long names can't exceed
// Number.MAX_SAFE_INTEGER and skew the color distribution. Can't use the
// usual `| 0` 32-bit truncation trick here — this repo's ESLint config
// bans bitwise operators.
const HASH_MODULUS = 2147483647;

const hashString = (value: string): number =>
  value
    .split("")
    .reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % HASH_MODULUS, 0);

const Avatar: FC<AvatarProps> = ({ name, variant = "gradient" }) => {
  const colorClass =
    variant === "colored"
      ? COLORED_VARIANT_CLASSES[
          Math.abs(hashString(name)) % COLORED_VARIANT_CLASSES.length
        ]
      : undefined;

  return (
    <div className={[styles.avatar, colorClass].filter(Boolean).join(" ")}>
      {getInitials(name)}
    </div>
  );
};

export default Avatar;
